import type {
  MessageAttachment,
  OutgoingAttachment,
} from '../../integration/types.js';

import type { ComposerAttachment } from './composer/index.js';

/** Attachment `type` the bubble renders by, guessed from the mime when the upload did not report one. */
function attachmentType(
  attachment: ComposerAttachment,
): MessageAttachment['type'] {
  if (attachment.uploaded !== undefined) return attachment.uploaded.type;
  if (attachment.mime.startsWith('image/')) return 'image';
  return attachment.mime === 'application/pdf' ? 'document' : 'file';
}

/**
 * One uploaded chip as the send carries it. The url is a local object URL so the optimistic bubble can
 * paint the image before the server has anything to serve; a chip with no local bytes gets no url,
 * which is only reachable through the artifact path above and never for an upload. A stored draft
 * asks for no preview: an object URL dies with the page, so it would only be a dead link on reload.
 */
function fromUpload(
  attachment: ComposerAttachment,
  withPreviewUrl: boolean,
): MessageAttachment {
  return {
    id: attachment.uploaded?.s3Key ?? attachment.id,
    name: attachment.uploaded?.filename ?? attachment.name,
    contentType: attachment.uploaded?.contentType ?? attachment.mime,
    mime: attachment.uploaded?.contentType ?? attachment.mime,
    type: attachmentType(attachment),
    size: attachment.uploaded?.size ?? attachment.size,
    url:
      !withPreviewUrl || attachment.file === undefined
        ? undefined
        : URL.createObjectURL(attachment.file),
  };
}

/** One chip in the shape the server tells apart by keys alone; see {@link toOutgoingAttachments}. */
function outgoingAttachment(
  attachment: ComposerAttachment,
  withPreviewUrl: boolean,
): OutgoingAttachment {
  return (
    attachment.oneDriveRef ??
    attachment.sharePointRef ??
    attachment.artifactRef ??
    fromUpload(attachment, withPreviewUrl)
  );
}

/**
 * Whether a chip still means something after the composer that holds it goes away.
 *
 * The home screen submits and then navigates to the chat, so an upload still in flight is lost: its
 * bytes were going to S3 from a page that no longer exists. Both reference kinds survive by
 * construction — they carry an address and never had bytes of their own to finish sending.
 *
 * It lives here, next to the mapping, because the two answer one question between them and drifted
 * apart once already: the home screen kept its own copy of both, and when a third kind of attachment
 * arrived the copy silently dropped it. The chip was on screen and the message left without it.
 */
export function survivesSubmitRedirect(
  attachment: ComposerAttachment,
): boolean {
  if (attachment.oneDriveRef !== undefined) return true;
  if (attachment.sharePointRef !== undefined) return true;
  if (attachment.artifactRef !== undefined) return true;
  return (
    attachment.uploadState === 'uploaded' && attachment.uploaded !== undefined
  );
}

/**
 * Map the composer's chips onto what a send puts in `message.attachments`.
 *
 * Three kinds, and the two reference kinds are passed through untouched — each one must keep exactly the
 * keys it was built with, because the server tells them apart by shape alone. A workspace file is
 * recognised by `artifact_id` with no `id` (see `artifactAttachmentRef`); a SharePoint file by
 * `drive_id` + `item_id` with neither of those, which is why it is checked FIRST: adding a key here
 * would move it into a branch that keeps the file name and throws the address away.
 *
 * Every send goes through this one function. A second mapping written beside it is how a kind gets
 * lost — see {@link survivesSubmitRedirect}.
 */
export function toOutgoingAttachments(
  attachments: ComposerAttachment[],
): OutgoingAttachment[] {
  return attachments.map((attachment) => outgoingAttachment(attachment, true));
}

/**
 * The chips a stored draft can bring back: the same mapping as a send, without the local preview URL,
 * and only for chips that mean something once this page is gone (see {@link survivesSubmitRedirect}).
 */
export function toDraftAttachments(
  attachments: ComposerAttachment[],
): OutgoingAttachment[] {
  return attachments
    .filter(survivesSubmitRedirect)
    .map((attachment) => outgoingAttachment(attachment, false));
}
